import type { MouseEvent } from "react";

import type { Activity } from "../../api/types";
import { Panel } from "../../components/ui/Panel";
import { formatTimeIn, formatTimeRange } from "../../lib/dates";
import { LANES, laneOf } from "../../lib/sources";
import { type BlockKind, type BlockView, activityEnd, activityTitle } from "./model";
import { nowAt, place, ribbonFor, stacking } from "./ribbon";
import { CAPSULE_TONES as TICKET_TONES } from "./tickets";

const CAPSULE_TONES: Record<BlockKind, string> = {
  needs_client:
    "border-status-needs-client-line bg-status-needs-client-bg text-status-needs-client-fg",
  in_progress:
    "border-status-in-progress-line bg-status-in-progress-bg text-status-in-progress-fg",
  ready: "border-line bg-card text-text",
  not_logged:
    "border-status-needs-client-line bg-status-needs-client-bg text-status-needs-client-fg",
  logging:
    "border-status-in-progress-line bg-status-in-progress-bg text-status-in-progress-fg",
  // A logged block whose ticket isn't known: as one whose status isn't.
  logged: TICKET_TONES.unknown,
  dismissed: "border-line bg-track text-muted",
};

/**
 * A capsule's colours by its status; a logged block's by its ticket's state
 * (v0.9.0's P2b): the logged green now means closed, and an open ticket's
 * capsule has an amber outline.
 */
function toneOf(block: BlockView): string {
  return block.kind === "logged" && block.ticket !== null ? TICKET_TONES[block.ticket.state] : CAPSULE_TONES[block.kind];
}

function at(percent: number) {
  return `${String(percent)}%`;
}

const NO_IDS: ReadonlySet<string> = new Set();

/**
 * The whole day, left to right (section 11.0): block capsules colored by
 * status on top (a logged one's by its ticket: green closed, amber-outlined
 * open), one lane per source below, hour lines and a "now" marker.
 * Clicking a capsule opens its block's popup (ADR 0184), whose block is the
 * selected one. Capsules share one row: a shorter one
 * draws over a longer one, and the selected one over all (#251). They're out
 * of the tab order, since each block's card does the same, and their labels
 * still reach a screen reader (Idea #287). The hours stretch around what's
 * drawn, and nothing else. A right-click on a capsule opens its card's menu
 * (ADR 0194, decision 3).
 */
export function RibbonTimeline({
  day,
  timeZone,
  blocks,
  activities,
  faded = NO_IDS,
  selectedId,
  onSelect,
  onMenu,
  now,
}: {
  day: string;
  timeZone: string;
  blocks: readonly BlockView[];
  /** The activities to draw in the lanes (`ribbonActivities`). */
  activities: readonly Activity[];
  /** Those whose pills are faded, as a dismissed block's card is (ADR 0094). */
  faded?: ReadonlySet<string>;
  selectedId: string | null;
  /** Opens the block's popup (ADR 0184). */
  onSelect: (id: string) => void;
  /** A right-click on a block's capsule: its card's menu, or the browser's own (the day view decides). */
  onMenu?: ((id: string, event: MouseEvent<HTMLElement>) => void) | undefined;
  /** Where the "now" marker goes; null for a day other than today. */
  now: Date | null;
}) {
  const ribbon = ribbonFor(day, timeZone, [
    ...blocks.map((b) => ({ start: b.startedAt, end: b.endedAt })),
    ...activities.map((a) => ({ start: a.started_at, end: activityEnd(a) })),
  ]);
  const marker = now === null ? null : nowAt(ribbon, now);
  const lastHour = ribbon.hours.length - 1;
  const capsules = blocks.map((block) => ({ block, ...place(ribbon, block.startedAt, block.endedAt) }));
  const layers = stacking(capsules.map(({ block, width }) => ({ id: block.id, width })), selectedId);

  return (
    <Panel aria-label="Timeline" className="flex flex-col gap-2 px-5 pt-3.5 pb-4">
      <div aria-hidden="true" className="relative ml-24 h-4">
        {ribbon.hours.map((hour, i) => (
          <span
            key={hour.label + String(i)}
            className={
              "absolute top-0 font-mono text-[11px] whitespace-nowrap text-muted " +
              // Each label starts at its line, as in the mockup; the last ends at it.
              (i === lastHour ? "-translate-x-full" : "")
            }
            style={{ left: at(hour.at) }}
          >
            {hour.label}
          </span>
        ))}
      </div>
      <div className="relative flex flex-col gap-2">
        <div aria-hidden="true" className="pointer-events-none absolute -top-1 bottom-0 left-24 right-0">
          {ribbon.hours.map((hour, i) => (
            <span
              key={hour.label + String(i)}
              className="absolute inset-y-0 border-l border-grid"
              style={{ left: at(hour.at) }}
            />
          ))}
        </div>
        <div className="flex">
          <span className="w-24 shrink-0 text-xs leading-[30px] font-[650]">Blocks</span>
          {/* Isolated, so the capsules' z-indexes stay in this row, under the now marker. */}
          <div className="relative isolate h-[30px] min-w-0 flex-1">
            {capsules.map(({ block, left, width }) => {
              const selected = block.id === selectedId;
              // What it shows ("Pinecrest Law · 40m") first, so its name holds its text (WCAG 2.5.3).
              const name = `${block.title}, ${block.duration}, ${formatTimeRange(block.startedAt, block.endedAt, timeZone)}, ${block.statusLabel}`;
              return (
                <button
                  key={block.id}
                  type="button"
                  aria-pressed={selected}
                  aria-label={name}
                  title={name}
                  tabIndex={-1}
                  onClick={() => {
                    onSelect(block.id);
                  }}
                  onContextMenu={(event) => {
                    onMenu?.(block.id, event);
                  }}
                  className={
                    "absolute top-0 flex h-[30px] min-w-1.5 cursor-pointer items-center overflow-hidden rounded-capsule border text-xs font-semibold whitespace-nowrap " +
                    (selected ? "border-brand bg-brand text-white" : toneOf(block))
                  }
                  style={{ left: at(left), width: at(width), zIndex: layers.get(block.id) }}
                >
                  {/* The padding is inside, so a short block can still be 6px wide. */}
                  <span className="truncate px-2">{block.capsuleLabel}</span>
                </button>
              );
            })}
          </div>
        </div>
        {LANES.map(({ lane, label }) => (
          <div key={lane} className="flex items-center">
            <span className="w-24 shrink-0 text-[11.5px] text-muted">{label}</span>
            <div className="relative h-3 min-w-0 flex-1 rounded-pill bg-track">
              {activities
                .filter((a) => laneOf(a.source) === lane)
                .map((activity) => {
                  const { left, width } = place(ribbon, activity.started_at, activityEnd(activity));
                  const dismissed = faded.has(activity.id);
                  return (
                    <span
                      key={activity.id}
                      // Never the fade alone: the hover says why (section 11.0).
                      title={
                        `${activityTitle(activity)}, ${formatTimeRange(activity.started_at, activity.ended_at, timeZone)}` +
                        (dismissed ? ", Dismissed" : "")
                      }
                      className={"absolute top-0 h-3 min-w-1.5 rounded-pill bg-lane" + (dismissed ? " opacity-60" : "")}
                      style={{ left: at(left), width: at(width) }}
                    />
                  );
                })}
            </div>
          </div>
        ))}
        {now !== null && marker !== null ? (
          <div className="pointer-events-none absolute -top-2 -bottom-1 left-24 right-0">
            <span
              role="img"
              aria-label={`Now, ${formatTimeIn(now.toISOString(), timeZone)}`}
              className="absolute inset-y-0 w-[3px] -translate-x-1/2 rounded-pill bg-now"
              style={{ left: at(marker) }}
            />
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
